
import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, UserCheck } from "lucide-react";
import { useCreateStaff } from "@/hooks/use-staff";
import { useCrmAccess } from "@/hooks/use-crm-access";
import { useToast } from "@/hooks/use-toast";
import { useAvailableUsersForStaff } from "@/hooks/use-available-users-for-staff";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";

export const StaffModal = () => {
  const [open, setOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<string>("");
  const [formData, setFormData] = useState({
    role: "staff",
    permissions: "staff",
    title: "",
    details: "",
  });

  const { data: hasCrmAccess } = useCrmAccess();
  const { data: availableUsers = [], isLoading: usersLoading } = useAvailableUsersForStaff();
  const createStaff = useCreateStaff();
  const { toast } = useToast();

  if (!hasCrmAccess) {
    return null;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedUserId) {
      toast({
        title: "User Required",
        description: "Please select a user to promote to staff",
        variant: "destructive",
      });
      return;
    }

    const selectedUser = availableUsers.find(user => user.user_id === selectedUserId);
    if (!selectedUser) {
      toast({
        title: "Invalid Selection",
        description: "Selected user not found",
        variant: "destructive",
      });
      return;
    }

    console.log("Creating staff from existing user:", selectedUser);
    
    try {
      const staffData = {
        ...formData,
        first_name: selectedUser.first_name,
        last_name: selectedUser.last_name,
        email: selectedUser.email,
        phone: selectedUser.phone,
        profile_image_url: selectedUser.avatar_url,
        user_id: selectedUser.user_id,
        existing_user_mode: true,
      };

      const result = await createStaff.mutateAsync(staffData);
      console.log("Staff created successfully:", result);
      setOpen(false);
      setSelectedUserId("");
      setFormData({
        role: "staff",
        permissions: "staff",
        title: "",
        details: "",
      });
    } catch (error: any) {
      console.error("Error creating staff:", error);
      toast({
        title: "Error",
        description: error?.message || "Failed to create staff member. Please try again.",
        variant: "destructive",
      });
    }
  };

  const selectedUser = availableUsers.find(user => user.user_id === selectedUserId);
  
  const getInitials = (firstName: string, lastName: string) => {
    if (!firstName && !lastName) return "ST";
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="bg-teal-primary hover:bg-teal-dark text-black font-semibold rounded-lg shadow-md">
          <Plus className="h-4 w-4 mr-2" />
          Add Staff Member
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-2xl bg-slate-50 border-slate-200 rounded-xl shadow-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader className="bg-white -mx-6 -mt-6 px-6 py-4 rounded-t-xl">
          <DialogTitle className="text-foreground text-xl font-semibold">Add Staff Member</DialogTitle>
        </DialogHeader>
        
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-6">
          <div className="flex items-start gap-3">
            <UserCheck className="h-5 w-5 text-blue-600 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-blue-800">Simplified Staff Onboarding</p>
              <p className="text-sm text-blue-700 mt-1">
                Select an existing user who has already been onboarded to promote them to staff. They'll immediately get access to their new role.
              </p>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6 mt-6">
          {/* User Selection */}
          <div className="space-y-2">
            <Label className="text-slate-800 font-medium">Select User to Promote *</Label>
            {usersLoading ? (
              <div className="text-slate-600 py-4">Loading available users...</div>
            ) : availableUsers.length === 0 ? (
              <div className="text-slate-600 py-4 text-center">
                No available users found. Users must be onboarded through the normal signup process first.
              </div>
            ) : (
              <Select value={selectedUserId} onValueChange={setSelectedUserId}>
                <SelectTrigger className="border-slate-300 focus:border-teal-500 rounded-lg">
                  <SelectValue placeholder="Choose a user to promote to staff" />
                </SelectTrigger>
                <SelectContent className="bg-slate-50 border-slate-200 rounded-lg max-h-60">
                  {availableUsers.map((user) => (
                    <SelectItem key={user.user_id} value={user.user_id} className="hover:bg-teal-50">
                      <div className="flex items-center gap-3">
                        <Avatar className="w-6 h-6">
                          <AvatarImage src={user.avatar_url} />
                          <AvatarFallback className="bg-slate-200 text-slate-600 text-xs">
                            {getInitials(user.first_name, user.last_name)}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <div className="font-medium">{user.first_name} {user.last_name}</div>
                          <div className="text-sm text-slate-600">{user.email}</div>
                        </div>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          {/* Show selected user details */}
          {selectedUser && (
            <div className="bg-slate-100 rounded-lg p-4">
              <div className="flex items-center gap-3">
                <Avatar className="w-12 h-12">
                  <AvatarImage src={selectedUser.avatar_url} />
                  <AvatarFallback className="bg-slate-300 text-slate-700">
                    {getInitials(selectedUser.first_name, selectedUser.last_name)}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <div className="font-medium text-slate-800">{selectedUser.first_name} {selectedUser.last_name}</div>
                  <div className="text-sm text-slate-600">{selectedUser.email}</div>
                  {selectedUser.phone && (
                    <div className="text-sm text-slate-600">{selectedUser.phone}</div>
                  )}
                </div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

            <div className="space-y-2">
              <Label htmlFor="title" className="text-slate-800 font-medium">Job Title</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="border-slate-300 focus:border-teal-500 focus:ring-teal-500 rounded-lg"
                placeholder="Enter job title"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="permissions" className="text-slate-800 font-medium">Staff Role & Permissions</Label>
              <Select 
                value={formData.permissions} 
                onValueChange={(value) => setFormData({ ...formData, permissions: value, role: value })}
              >
                <SelectTrigger className="border-slate-300 focus:border-teal-500 rounded-lg">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-50 border-slate-200 rounded-lg">
                  <SelectItem value="staff" className="hover:bg-teal-50">Staff</SelectItem>
                  <SelectItem value="manager" className="hover:bg-teal-50">Manager</SelectItem>
                  <SelectItem value="admin" className="hover:bg-teal-50">Admin</SelectItem>
                  <SelectItem value="superuser" className="hover:bg-teal-50">Superuser</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="details" className="text-slate-800 font-medium">Additional Details</Label>
              <Textarea
                id="details"
                value={formData.details}
                onChange={(e) => setFormData({ ...formData, details: e.target.value })}
                className="border-slate-300 focus:border-teal-500 focus:ring-teal-500 rounded-lg"
                placeholder="Enter any additional details about this staff member..."
                rows={3}
              />
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-6 border-t border-slate-200">
            <Button
              type="button"
              variant="outline"
              onClick={() => setOpen(false)}
              className="border-slate-300 text-slate-700 hover:bg-slate-100 rounded-lg"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={createStaff.isPending || !selectedUserId || availableUsers.length === 0}
              className="font-semibold rounded-lg shadow-md disabled:opacity-50 text-black"
              style={{ backgroundColor: '#DFEF97' }}
              onMouseEnter={(e) => (e.target as HTMLElement).style.backgroundColor = '#d1e587'}
              onMouseLeave={(e) => (e.target as HTMLElement).style.backgroundColor = '#DFEF97'}
            >
              {createStaff.isPending ? "Promoting..." : "Promote to Staff"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
